import MomentUtils from "@date-io/moment";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Radio from "@material-ui/core/Radio";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import ClearIcon from "@material-ui/icons/Clear";
import MenuItem from "@material-ui/core/MenuItem";
import { MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import uuidv4 from "uuid/v4";

import PropertiesSearchDialog from "../PropertiesSearch";
import UsersSearchDialog from "../UsersSearch";
import LoadingButton from "../../Buttons/Loading";
import Divider from "../../Dividers/Dialog";
import Header from "../../Typography/Header";
import * as constants from "../../../constants";
import { createShiftTemplate } from "../../../utils/appsyncOperations";
import getUserLevelAvatar from "../../../utils/getUserLevelAvatar";
import * as actions from "../../../store/actions";
import PostOrders from "../../Lists/PostOrders";
import { init } from "../../../store/features/postOrders";

const CreateTemplateDialog = ({ open, onClose }) => {
  const [openShift, setOpenShift] = React.useState(false);
  const [userId, setUserId] = React.useState(null);
  const [displayName, setDisplayName] = React.useState("");
  const [levels, setLevels] = React.useState({
    one: false,
    two: false,
    three: false,
  });
  const nowMoment = moment();
  const [startTime, setStartTime] = React.useState(nowMoment);
  const [endTime, setEndTime] = React.useState(nowMoment);
  const timezoneGuess = momentTimezone.tz.guess(true);
  const [timezone, setTimezone] = React.useState(timezoneGuess);
  const [duration, setDuration] = useState(0);
  const [createPending, setCreatePending] = React.useState(false);
  const [propertiesSearchDialog, setPropertiesSearchDialog] = React.useState({
    open: false,
  });
  const [usersSearchDialog, setUsersSearchDialog] = React.useState({
    open: false,
  });

  const dispatch = useDispatch();
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const postOrders = useSelector((state) => state.postOrders.postOrders);
  const serviceProviderId = useSelector(
    (state) => state.serviceProvider.serviceProvider.serviceProviderId
  );
  const properties = useSelector((state) => state.properties.byId);
  const users = useSelector((state) => state.users.activeById);

  React.useEffect(() => {
    dispatch(init({ postOrders: [] }));
    if (open) {
      setOpenShift(false);
      setUserId(null);
      setLevels({
        one: false,
        two: false,
        three: false,
      });
      setStartTime(moment().add(1, "hours").startOf("hour"));
      setEndTime(moment().add(9, "hours").startOf("hour"));
      setTimezone(timezoneGuess);
      setDisplayName("");
    }
  }, [open]);

  useEffect(() => {
    const diff = endTime.diff(startTime);
    if (diff > 0) {
      setDuration(diff / 3600000);
    } else {
      setDuration(0);
    }
  }, [startTime, endTime]);

  const openPropertiesSearch = () => {
    if (!createPending) {
      setPropertiesSearchDialog({ open: true });
    }
  };
  const closePropertiesSearch = () =>
    setPropertiesSearchDialog({ open: false });
  const openUsersSearch = () => {
    if (!openShift && !createPending) {
      setUsersSearchDialog({ open: true });
    }
  };
  const closeUsersSearch = () => setUsersSearchDialog({ open: false });

  const user = users[userId];

  const classes = useStyles();

  const createTemplate = async () => {
    if (!displayName)
      return enqueueSnackbar("You must enter a template name!", {
        variant: "error",
      });
    if (!duration)
      return enqueueSnackbar("You must enter a duration greater than 0!", {
        variant: "error",
      });

    let requiredLevels = [];
    let tempUserId = userId;
    if (openShift) {
      if (levels.one) requiredLevels.push("LEVEL_1");
      if (levels.two) requiredLevels.push("LEVEL_2");
      if (levels.three) requiredLevels.push("LEVEL_3");
      tempUserId = null;
    } else {
      requiredLevels = null;
    }

    try {
      setCreatePending(true);
      const template = await createShiftTemplate({
        displayName,
        templateId: uuidv4(),
        time: {
          startMinutes: startTime.minutes(),
          startHours: startTime.hours(),
          duration: Math.floor(duration * 3600000), // hours to ms
        },
        userId: tempUserId,
        requiredLevels,
        serviceProviderId,
        timezone,
        postOrders,
      });
      setCreatePending(false);
      dispatch(actions.templates.setTemplate(template));
      onClose();
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      setCreatePending(false);
      console.error(e);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{"New template"}</DialogTitle>
        <DialogContent>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Header label="Name" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant={"outlined"}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  fullWidth
                  label="Template Name"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Header label="Time" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  fullWidth
                  label="Time zone"
                  select
                  SelectProps={{
                    MenuProps: {
                      style: styles.menus,
                    },
                  }}
                  variant="outlined"
                >
                  {[timezoneGuess, ...Object.values(constants.timezones)].map(
                    (timezone) => (
                      <MenuItem key={timezone} value={timezone}>
                        {timezone}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TimePicker
                  value={startTime}
                  onChange={(moment) => setStartTime(moment)}
                  ampm={false}
                  fullWidth
                  inputVariant="outlined"
                  label="Start time"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  type="number"
                  value={duration !== NaN ? duration : ""}
                  onChange={(e) =>
                    setDuration(Math.abs(parseFloat(e.target.value)))
                  }
                  fullWidth
                  label="Duration"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Hours</InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Header label="Assignee" />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormControlLabel
                    checked={!openShift}
                    onChange={(event) => setOpenShift(!event.target.checked)}
                    control={<Radio color="secondary" />}
                    label="Assign to an employee"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={9}>
                <TextField
                  value={user !== undefined ? user.displayName : ""}
                  onClick={openUsersSearch}
                  disabled={openShift || createPending}
                  fullWidth
                  label="Person"
                  InputProps={{
                    endAdornment:
                      user !== undefined && !openShift ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              setUserId(null);
                            }}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  value={
                    user !== undefined
                      ? getUserLevelAvatar(user.officerLevel)
                      : ""
                  }
                  disabled
                  fullWidth
                  label="Officer level"
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormControlLabel
                    checked={openShift}
                    onChange={(event) => setOpenShift(event.target.checked)}
                    control={<Radio color="secondary" />}
                    label="Create an open shift"
                  />
                </FormControl>
                <FormControl
                  className={classes.formControl}
                  component="fieldset"
                  disabled={!openShift}
                  fullWidth
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={levels.one}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setLevels((prevState) => ({
                              ...prevState,
                              one: checked,
                            }));
                          }}
                          value="one"
                        />
                      }
                      label="Level 1"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={levels.two}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setLevels((prevState) => ({
                              ...prevState,
                              two: checked,
                            }));
                          }}
                          value="two"
                        />
                      }
                      label="Level 2"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={levels.three}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setLevels((prevState) => ({
                              ...prevState,
                              three: checked,
                            }));
                          }}
                          value="three"
                        />
                      }
                      label="Level 3"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <PostOrders timezone={timezone} isEditable={true} />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={createTemplate}
            isLoading={createPending}
            color="primary"
          >
            Create
          </LoadingButton>
          <Button color="primary" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {open && (
        <>
          <UsersSearchDialog
            showHours={false}
            open={usersSearchDialog.open}
            onClose={closeUsersSearch}
            onUserClick={(userId) => {
              setUserId(userId);
              closeUsersSearch();
            }}
            users={Object.values(users)}
          />
        </>
      )}
    </>
  );
};

const styles = {
  menu: {
    width: 200,
  },
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

export default CreateTemplateDialog;
